import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, Search, Package, Truck, Store, User, Phone, Mail, MapPin,
  Calendar, Clock, CreditCard, MessageSquarePlus, ChevronDown, IndianRupee
} from 'lucide-react';
import { apiUrl, adminFetch } from '../../api';

/* Status options differ by fulfilment type. Values must match the server enums. */
const STATUS_OPTIONS = {
  local: ['confirmed', 'processing', 'out_for_delivery', 'delivered', 'cancelled'],
  pickup: ['confirmed', 'ready_for_pickup', 'picked_up', 'cancelled'],
};

const STATUS_LABEL = {
  confirmed: 'Confirmed',
  processing: 'Processing',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  ready_for_pickup: 'Ready for pickup',
  picked_up: 'Picked up',
  cancelled: 'Cancelled',
};

const STATUS_CLASS = {
  confirmed: 'st-confirmed',
  processing: 'st-processing',
  out_for_delivery: 'st-processing',
  ready_for_pickup: 'st-processing',
  delivered: 'st-done',
  picked_up: 'st-done',
  cancelled: 'st-cancelled',
};

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(d); }
};

const fmtDay = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return String(d); }
};

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // all | local | pickup
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const admin = (() => {
    try { return JSON.parse(localStorage.getItem('admin')); } catch { return null; }
  })();

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [hdRes, spRes, statsRes] = await Promise.all([
        adminFetch(apiUrl('/api/orders/home-delivery?limit=500')),
        adminFetch(apiUrl('/api/orders/store-pickup?limit=500')),
        adminFetch(apiUrl('/api/orders/stats')),
      ]);
      const [hd, sp, st] = await Promise.all([hdRes.json(), spRes.json(), statsRes.json()]);

      const hdOrders = (hd?.data?.orders || []).map((o) => ({ ...o, deliveryType: 'local' }));
      const spOrders = (sp?.data?.orders || []).map((o) => ({ ...o, deliveryType: 'pickup' }));

      const all = [...hdOrders, ...spOrders].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setOrders(all);
      if (st?.success) setStats(st.data);
    } catch {
      setError('Could not load orders. Is the server running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const updateStatus = async (order, status) => {
    // optimistic update
    setOrders((prev) =>
      prev.map((o) => (o.orderId === order.orderId ? { ...o, orderStatus: status } : o))
    );
    try {
      const res = await adminFetch(apiUrl(`/api/orders/${order.orderId}/status`), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, deliveryType: order.deliveryType }),
      });
      const data = await res.json();
      if (!data.success) throw new Error();
      loadOrders(); // refresh stats
    } catch {
      setError('Failed to update status.');
      loadOrders();
    }
  };

  const addNote = async (order, text, reset) => {
    if (!text.trim()) return;
    try {
      const res = await adminFetch(apiUrl(`/api/orders/${order.orderId}/notes`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          deliveryType: order.deliveryType,
          author: admin?.name || admin?.email || 'Admin',
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error();
      setOrders((prev) =>
        prev.map((o) => (o.orderId === order.orderId ? { ...o, notes: data.data.notes } : o))
      );
      reset();
    } catch {
      setError('Failed to add note.');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((o) => {
      if (typeFilter !== 'all' && o.deliveryType !== typeFilter) return false;
      if (statusFilter !== 'all' && o.orderStatus !== statusFilter) return false;
      if (q) {
        const hay = [
          o.orderId, o.customerInfo?.name, o.customerInfo?.email, o.customerInfo?.phone,
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [orders, typeFilter, statusFilter, search]);

  return (
    <div className="admin-shell">
      <main className="admin-main">
        <div className="admin-main-header ao-header">
          <h2>Orders</h2>
          <button className="admin-btn-ghost dark" onClick={loadOrders} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'ao-spin' : ''} /> Refresh
          </button>
        </div>

        {error && <div className="admin-error">{error}</div>}

        {/* Stats */}
        {stats && (
          <div className="ao-stats">
            <StatCard label="Total orders" value={stats.totalOrders} />
            <StatCard label="Active" value={stats.active} tone="processing" />
            <StatCard label="Completed" value={stats.completed} tone="done" />
            <StatCard label="Cancelled" value={stats.cancelled} tone="cancelled" />
            <StatCard label="Revenue" value={money(stats.revenue)} tone="revenue" />
          </div>
        )}

        {/* Filters */}
        <div className="ao-toolbar">
          <div className="ao-search">
            <Search size={16} />
            <input
              placeholder="Search by order ID, name, email or phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="ao-seg">
            {[['all', 'All'], ['local', 'Home delivery'], ['pickup', 'Store pickup']].map(([v, l]) => (
              <button
                key={v}
                className={`ao-seg-btn ${typeFilter === v ? 'on' : ''}`}
                onClick={() => setTypeFilter(v)}
              >{l}</button>
            ))}
          </div>
          <select
            className="admin-input sm ao-status-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            {Object.keys(STATUS_LABEL).map((s) => (
              <option key={s} value={s}>{STATUS_LABEL[s]}</option>
            ))}
          </select>
        </div>

        {/* List */}
        {loading ? (
          <p className="admin-muted">Loading orders…</p>
        ) : filtered.length === 0 ? (
          <div className="ao-empty">
            <Package size={34} />
            <p>No orders match your filters.</p>
          </div>
        ) : (
          <div className="ao-list">
            {filtered.map((order) => (
              <OrderCard
                key={order.orderId}
                order={order}
                expanded={expanded === order.orderId}
                onToggle={() =>
                  setExpanded((cur) => (cur === order.orderId ? null : order.orderId))
                }
                onStatus={(s) => updateStatus(order, s)}
                onAddNote={(text, reset) => addNote(order, text, reset)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  return (
    <div className={`ao-stat ${tone ? `tone-${tone}` : ''}`}>
      <span className="ao-stat-value">{value}</span>
      <span className="ao-stat-label">{label}</span>
    </div>
  );
}

function OrderCard({ order, expanded, onToggle, onStatus, onAddNote }) {
  const [noteText, setNoteText] = useState('');
  const isLocal = order.deliveryType === 'local';
  const fulfil = isLocal ? order.deliveryDetails : order.pickupDetails;
  const notes = order.notes || [];

  return (
    <div className={`ao-card ${expanded ? 'open' : ''}`}>
      <button className="ao-card-head" onClick={onToggle}>
        <div className="ao-card-head-main">
          <span className={`ao-type ${isLocal ? 'local' : 'pickup'}`}>
            {isLocal ? <Truck size={13} /> : <Store size={13} />}
            {isLocal ? 'Delivery' : 'Pickup'}
          </span>
          <div className="ao-card-id">
            <strong>{order.orderId}</strong>
            <span className="ao-card-sub">{order.customerInfo?.name} · {fmtDate(order.createdAt)}</span>
          </div>
        </div>
        <div className="ao-card-head-right">
          <span className="ao-card-total">{money(order.pricing?.total)}</span>
          <span className={`ao-badge ${STATUS_CLASS[order.orderStatus] || ''}`}>
            {STATUS_LABEL[order.orderStatus] || order.orderStatus}
          </span>
          <ChevronDown size={18} className="ao-chevron" />
        </div>
      </button>

      {expanded && (
        <div className="ao-card-body">
          <div className="ao-grid">
            {/* Customer */}
            <section className="ao-panel">
              <h4>Customer</h4>
              <p><User size={14} /> {order.customerInfo?.name}</p>
              <p><Mail size={14} /> <a href={`mailto:${order.customerInfo?.email}`}>{order.customerInfo?.email}</a></p>
              <p><Phone size={14} /> <a href={`tel:${order.customerInfo?.phone}`}>{order.customerInfo?.phone}</a></p>
              {order.customerInfo?.address && <p><MapPin size={14} /> {order.customerInfo.address}</p>}
            </section>

            {/* Fulfilment */}
            <section className="ao-panel">
              <h4>{isLocal ? 'Delivery' : 'Pickup'} details</h4>
              <p><Calendar size={14} /> {fmtDay(fulfil?.selectedDate)}</p>
              <p><Clock size={14} /> {fulfil?.selectedTimeSlot || '—'}</p>
              {isLocal && (
                <>
                  <p><MapPin size={14} /> {fulfil?.deliveryArea} · {fulfil?.pinCode}</p>
                  <p><Truck size={14} /> Delivery charge: {money(fulfil?.deliveryCharge)}</p>
                </>
              )}
            </section>

            {/* Payment */}
            <section className="ao-panel">
              <h4>Payment</h4>
              <p><CreditCard size={14} /> <span className={`ao-badge sm ${order.paymentStatus === 'paid' ? 'st-done' : 'st-cancelled'}`}>{order.paymentStatus}</span></p>
              <p className="ao-mono">Payment ID: {order.razorpayPaymentId || '—'}</p>
              <p className="ao-mono">Order ID: {order.razorpayOrderId || '—'}</p>
            </section>
          </div>

          {/* Products */}
          <section className="ao-panel">
            <h4>Items ({order.products?.length || 0})</h4>
            <div className="ao-items">
              {(order.products || []).map((p, i) => (
                <div className="ao-item" key={i}>
                  {p.image
                    ? <img src={p.image} alt={p.name} className="ao-item-img" />
                    : <div className="ao-item-img ao-item-noimg"><Package size={16} /></div>}
                  <div className="ao-item-info">
                    <span className="ao-item-name">{p.name}</span>
                    <span className="ao-item-meta">{p.variant} · Qty {p.quantity}</span>
                  </div>
                  <span className="ao-item-price">{money(p.price * p.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="ao-totals">
              <div><span>Subtotal</span><span>{money(order.pricing?.subtotal)}</span></div>
              {isLocal && <div><span>Delivery</span><span>{money(order.pricing?.deliveryCharge)}</span></div>}
              {order.pricing?.gst ? <div><span>GST</span><span>{money(order.pricing.gst)}</span></div> : null}
              <div className="ao-totals-final"><span>Total</span><span><IndianRupee size={13} />{Number(order.pricing?.total || 0).toLocaleString('en-IN')}</span></div>
            </div>
          </section>

          {/* Status + Notes */}
          <div className="ao-grid ao-grid-2">
            <section className="ao-panel">
              <h4>Update status</h4>
              <div className="ao-status-btns">
                {STATUS_OPTIONS[order.deliveryType].map((s) => (
                  <button
                    key={s}
                    className={`ao-status-pill ${STATUS_CLASS[s]} ${order.orderStatus === s ? 'active' : ''}`}
                    onClick={() => onStatus(s)}
                    disabled={order.orderStatus === s}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
            </section>

            <section className="ao-panel">
              <h4>Internal notes ({notes.length})</h4>
              <div className="ao-notes">
                {notes.length === 0 && <p className="admin-muted sm">No notes yet.</p>}
                {notes.map((n, i) => (
                  <div className="ao-note" key={i}>
                    <p>{n.text}</p>
                    <span className="ao-note-meta">{n.author || 'Admin'} · {fmtDate(n.createdAt)}</span>
                  </div>
                ))}
              </div>
              <div className="ao-note-form">
                <textarea
                  className="admin-input sm"
                  rows={2}
                  placeholder="Add a note or comment about this order…"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                />
                <button
                  className="admin-btn-primary sm"
                  onClick={() => onAddNote(noteText, () => setNoteText(''))}
                  disabled={!noteText.trim()}
                >
                  <MessageSquarePlus size={15} /> Add note
                </button>
              </div>
            </section>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminOrders;
